import { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { CaretLeft, DotsThreeVertical, Sun, Moon, Lightbulb } from 'phosphor-react';
import { theme } from '../../styles/theme';
import { useTranslation } from '../../i18n';
import { useTheme } from '../../hooks/useTheme';
import { trackSidebarButton } from '../../services/analytics.service';

interface Session {
  id: string;
  started_at: string;
  last_message_at: string;
  message_count: number;
  first_message?: string;
}

interface SideMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionSelect: (sessionId: string) => void;
  onNavigate?: (destination: 'insights') => void;
  userId?: string;
}

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${theme.zIndex.modal};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity ${theme.transitions.normal};
  animation: ${({ $isOpen }) => ($isOpen ? fadeIn : 'none')} ${theme.transitions.normal};
`;

const MenuContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 320px;
  background: color-mix(in srgb, var(--color-bg) 90%, transparent);
  border: 1px solid var(--color-border);
  backdrop-filter: blur(5px);
  border-radius: 0px 16px 16px 0px;
  z-index: ${theme.zIndex.modal + 1};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
  animation: ${({ $isOpen }) => ($isOpen ? slideIn : 'none')} ${theme.transitions.normal};
  transition: transform ${theme.transitions.normal};
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 24px 20px;
  padding-top: max(48px, env(safe-area-inset-top));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  font-family: ${theme.typography.fontFamily.primary};
`;

const IconButton = styled.button<{ $isRefreshing?: boolean }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-border-subtle);
  border-radius: ${theme.borderRadius.full};
  color: var(--color-text);
  transition: all ${theme.transitions.fast};

  &:hover {
    background: var(--color-border);
  }

  &:active {
    transform: scale(0.95);
  }

  ${({ $isRefreshing }) =>
    $isRefreshing &&
    `
    animation: spin 1s linear infinite;
    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `}
`;

const SessionsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 2px;
  }
`;

const DateGroup = styled.div`
  margin-bottom: 24px;
`;

const DateHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 20px;
  margin-bottom: 12px;
  font-family: ${theme.typography.fontFamily.primary};
`;

const SessionItem = styled.button`
  width: 100%;
  padding: 8px 20px;
  background: transparent;
  border: none;
  text-align: left;
  transition: all ${theme.transitions.fast};
  cursor: pointer;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  color: var(--color-text);
  line-height: 1.4;

  &:hover {
    background: var(--color-bg-elevated);
  }

  &:active {
    background: var(--color-bg-elevated);
  }
`;

const NavSection = styled.div`
  padding: 8px 0 12px;
  border-bottom: 1px solid var(--color-border-subtle);
  margin-bottom: 12px;
`;

const NavItem = styled.button`
  width: 100%;
  padding: 10px 20px;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
  transition: all ${theme.transitions.fast};

  svg {
    color: var(--color-primary);
    flex-shrink: 0;
  }

  &:hover {
    background: var(--color-bg-elevated);
  }

  &:active {
    background: var(--color-bg-elevated);
    transform: scale(0.99);
  }
`;

const EmptyState = styled.div`
  padding: ${theme.spacing.xl};
  text-align: center;
  color: var(--color-text-secondary);
  font-family: ${theme.typography.fontFamily.primary};
`;

const LoadingState = styled.div`
  padding: ${theme.spacing.xl};
  text-align: center;
  color: var(--color-text-secondary);
  font-family: ${theme.typography.fontFamily.primary};
`;

const ContextMenuContainer = styled.div`
  position: relative;
`;

const ContextMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 48px;
  right: 0;
  background: var(--color-card-bg);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 8px 0;
  min-width: 220px;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all ${theme.transitions.fast};
  z-index: ${theme.zIndex.modal + 2};
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const ContextMenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: var(--color-border-subtle);
  }

  &:active {
    background: var(--color-border);
  }

  svg {
    flex-shrink: 0;
  }
`;

export const SideMenu: React.FC<SideMenuProps> = ({ isOpen, onClose, onSessionSelect, onNavigate, userId }) => {
  const { t, lang } = useTranslation();
  const { mode: themeMode, toggle: toggleTheme } = useTheme();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen || !userId) return;
    const controller = new AbortController();
    fetchSessions(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  // Close on escape key (listener only attached while open)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Move focus into the menu on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    menuRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setIsContextMenuOpen(false);
      }
    };

    if (isContextMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isContextMenuOpen]);

  const fetchSessions = async (signal?: AbortSignal) => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await fetch(`/api/chat/sessions/${userId}?limit=20&offset=0`, {
        credentials: 'include',
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.status}`);
      }

      const data = await response.json();
      setSessions(data.data.sessions || []);
    } catch (error) {
      // Aborted requests (menu closed / unmounted) should not update state
      if ((error as Error)?.name === 'AbortError') return;
      console.error('Error fetching sessions:', error);
      setLoadError(true);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const getDateGroup = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return t('sideMenu.today');
    } else if (diffInHours < 48) {
      return t('sideMenu.yesterday');
    } else {
      return t('sideMenu.older');
    }
  };

  const groupSessionsByDate = () => {
    const groups: { [key: string]: Session[] } = {
      [t('sideMenu.today')]: [],
      [t('sideMenu.yesterday')]: [],
      [t('sideMenu.older')]: [],
    };

    sessions.forEach((session) => {
      const group = getDateGroup(session.last_message_at);
      groups[group].push(session);
    });

    return groups;
  };

  const handleSessionClick = (sessionId: string) => {
    onSessionSelect(sessionId);
    onClose();
  };

  const toggleContextMenu = () => {
    setIsContextMenuOpen(!isContextMenuOpen);
  };

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <MenuContainer
        $isOpen={isOpen}
        ref={menuRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="side-menu-title"
        tabIndex={-1}
      >
        <Header>
          <Title id="side-menu-title">{t('sideMenu.title')}</Title>
          <HeaderButtons>
            <ContextMenuContainer ref={contextMenuRef}>
              <IconButton
                onClick={toggleContextMenu}
                aria-label="More options"
                aria-haspopup="menu"
                aria-expanded={isContextMenuOpen}
              >
                <DotsThreeVertical size={24} weight="bold" />
              </IconButton>
              <ContextMenu $isOpen={isContextMenuOpen}>
                <ContextMenuItem onClick={() => {
                  // Fire an analytics event whenever the user changes theme —
                  // tag it with the theme they are switching TO so we can see
                  // both directions (dark mode / light mode).
                  trackSidebarButton(themeMode === 'dark' ? 'light mode' : 'dark mode');
                  toggleTheme();
                  setIsContextMenuOpen(false);
                }}>
                  {themeMode === 'dark' ? <Sun size={20} weight="bold" /> : <Moon size={20} weight="bold" />}
                  {themeMode === 'dark' ? t('chat.lightMode') : t('chat.darkMode')}
                </ContextMenuItem>
              </ContextMenu>
            </ContextMenuContainer>
            <IconButton onClick={onClose} aria-label="Close menu">
              <CaretLeft size={24} weight="bold" />
            </IconButton>
          </HeaderButtons>
        </Header>

        <SessionsList>
          {onNavigate && (
            <NavSection>
              <NavItem onClick={() => { trackSidebarButton('insights'); onNavigate('insights'); onClose(); }}>
                <Lightbulb size={18} weight="regular" />
                <span>{lang === 'tr' ? 'İçgörülerim' : 'My Insights'}</span>
              </NavItem>
            </NavSection>
          )}

          {loading ? (
            <LoadingState>{t('sideMenu.loading')}</LoadingState>
          ) : loadError ? (
            <EmptyState role="alert">
              {lang === 'tr' ? 'Sohbetler yüklenemedi. Lütfen tekrar deneyin.' : 'Failed to load conversations. Please try again.'}
            </EmptyState>
          ) : sessions.length === 0 ? (
            <EmptyState>{t('sideMenu.empty')}</EmptyState>
          ) : (
            <>
              {Object.entries(groupSessionsByDate()).map(([dateGroup, groupSessions]) =>
                groupSessions.length > 0 && (
                  <DateGroup key={dateGroup}>
                    <DateHeader>{dateGroup}</DateHeader>
                    {groupSessions.map((session) => (
                      <SessionItem
                        key={session.id}
                        onClick={() => handleSessionClick(session.id)}
                      >
                        {session.first_message || t('sideMenu.newConversation')}
                      </SessionItem>
                    ))}
                  </DateGroup>
                )
              )}
            </>
          )}
        </SessionsList>
      </MenuContainer>
    </>
  );
};
